/**
 * First-run attention orchestrator.
 *
 * Coordinates "moments that take over the whole screen" — the
 * orientation redirect, the FirstRunTour spotlight, the
 * portal-welcome overlay, the sign-in welcome flourish, and any
 * future attention-grabbing UX.
 *
 * Why this exists: each consumer used to manage its own gating
 * (separate localStorage flags, separate trigger events, separate
 * effect lifecycles). They worked by accident because their triggers
 * were disjoint, but the moment a new attention moment landed on the
 * same trigger as an existing one (e.g. "play overlay on Clerk
 * sign-in" coinciding with the FirstRunTour for legacy users), they
 * clobbered each other. The orchestrator serializes them so any
 * caller can request attention without coordinating with every other
 * caller.
 *
 * Contract for consumers:
 *
 * 1. Call `requestAttention({ id, priority?, shouldFire?, run })`.
 *    The returned promise resolves when YOUR `run()` resolves (or is
 *    skipped because `shouldFire()` returned false).
 *
 * 2. `run()` MUST resolve within MAX_RUN_MS (15s). If it doesn't,
 *    the orchestrator force-resolves to keep the queue moving and
 *    logs a console.error with your `id`. Don't return a promise
 *    that waits on a user click without an internal timeout — your
 *    moment will get cancelled.
 *
 * 3. The orchestrator inserts a 250ms gap between moments so the
 *    previous one's fade-out can finish painting before the next
 *    one's run() begins. You don't manage this yourself.
 *
 * 4. If you `requestAttention` with an `id` that's already in the
 *    queue or currently running, the second call is deduped — it
 *    resolves to the same promise as the first. Idempotent by id.
 *
 * 5. To check "is something else holding the screen right now"
 *    (e.g. to defer your own ambient animation): use
 *    `isAttentionActive()` or `useCurrentAttention()` (from
 *    useAttention.ts).
 *
 * Design notes:
 *
 * - No React dependency. The module is plain TS so it can be called
 *   from Convex callbacks, custom event listeners, or any imperative
 *   code path. React integration is a thin hook in useAttention.ts.
 * - No Redux. CLAUDE.md bans async middleware (no thunks, no
 *   listeners, no sagas). The orchestrator's natural state is an
 *   in-flight promise queue — not a JSON snapshot — so Redux is the
 *   wrong tool.
 * - Module-level state. Survives route changes, wizard unmounts, and
 *   AnimatePresence cleanup. Vite HMR resets the queue on hot
 *   reload (acceptable in dev; production never HMRs).
 */

export type AttentionId = string;

export interface AttentionRequest {
  /**
   * Stable identifier. Used for dedupe (two requests with the same
   * id while one is in flight resolve to the same promise) and for
   * subscribe notifications.
   */
  id: AttentionId;
  /**
   * Lower numbers run first when multiple are pending at the same
   * instant. Ties broken by request order (FIFO). Default: 50.
   *
   * Conventions in this codebase:
   *   - 0   orientation-redirect (must run before anything else)
   *   - 10  first-run-tour
   *   - 50  portal-welcome (default)
   *   - 20  signin-welcome (deliberately after tour)
   */
  priority?: number;
  /**
   * Optional async predicate. Called when it's this request's turn.
   * If it resolves false, `run` is skipped and the request's
   * promise resolves immediately (still success — "you weren't needed").
   *
   * Predicates are called LATE (at turn time, not at queue time) so
   * the consumer can read fresh state — e.g. "is this flag still
   * unset?" — instead of stale state from when the request was
   * submitted.
   */
  shouldFire?: () => boolean | Promise<boolean>;
  /**
   * The actual side effect. Must resolve within MAX_RUN_MS or it
   * will be force-resolved with a console.error — UNLESS
   * `userDriven` is set (see below).
   */
  run: () => Promise<void>;
  /**
   * Opt out of the MAX_RUN_MS cap for moments the user drives
   * through manually (e.g. the first-run tour, consent flows). The
   * cap exists to prevent a buggy `run()` from holding the queue
   * forever; for genuinely-interactive moments it just force-resolves
   * before the user can finish, advancing the queue underneath them.
   *
   * When true, `run()` may hold the queue indefinitely — callers must
   * eventually resolve it themselves (typically from a markComplete /
   * markDismissed handler the user reaches via the UI).
   */
  userDriven?: boolean;
}

/** Cap on how long any single non-user-driven `run()` can hold the queue. */
const MAX_RUN_MS = 15000;

/** Gap between draining one item and starting the next. */
const INTER_ITEM_GAP_MS = 250;

interface QueuedRequest {
  req: AttentionRequest;
  /** Resolver for the promise the caller is awaiting. */
  resolve: () => void;
}

const queue: QueuedRequest[] = [];
const inflightById = new Map<AttentionId, Promise<void>>();
let currentlyRunning: AttentionId | null = null;
let draining = false;

type Subscriber = (active: AttentionId | null) => void;
const subscribers = new Set<Subscriber>();

function notify(active: AttentionId | null): void {
  currentlyRunning = active;
  for (const sub of subscribers) {
    try {
      sub(active);
    } catch (err) {
      // Subscriber crashed; isolate so other subscribers + the
      // queue keep going. Log so we don't swallow it silently.
      // eslint-disable-next-line no-console
      console.error('[attention] subscriber threw', err);
    }
  }
}

/**
 * Wait `ms` milliseconds. Plain Promise<setTimeout>; resolves even
 * if the page is backgrounded (timers throttle but don't reject).
 */
function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function runWithTimeout(req: AttentionRequest): Promise<void> {
  // User-driven moments (first-run-tour, consent flows, etc.) opt out
  // of the cap — they only resolve when the user clicks through to
  // completion. Without this opt-out, a slow reader force-resolves at
  // 15s and the next attention moment lands on top of the still-open
  // tour overlay.
  if (req.userDriven) {
    try {
      await req.run();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[attention] "${req.id}" run() threw`, err);
    }
    return;
  }
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<void>((resolve) => {
    timeoutHandle = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.error(
        `[attention] "${req.id}" exceeded ${MAX_RUN_MS}ms; force-resolving to keep queue moving`,
      );
      resolve();
    }, MAX_RUN_MS);
  });
  try {
    await Promise.race([req.run(), timeoutPromise]);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`[attention] "${req.id}" run() threw`, err);
  } finally {
    if (timeoutHandle !== null) clearTimeout(timeoutHandle);
  }
}

async function drain(): Promise<void> {
  if (draining) return;
  draining = true;
  try {
    while (queue.length > 0) {
      // Pick highest-priority (lowest number); tie-break by insertion order.
      let bestIdx = 0;
      for (let i = 1; i < queue.length; i++) {
        const a = queue[bestIdx]!.req.priority ?? 50;
        const b = queue[i]!.req.priority ?? 50;
        if (b < a) bestIdx = i;
      }
      const [next] = queue.splice(bestIdx, 1);
      if (next === undefined) break; // defensive; shouldn't happen
      const { req, resolve } = next;

      let shouldRun = true;
      if (req.shouldFire !== undefined) {
        try {
          shouldRun = await req.shouldFire();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`[attention] "${req.id}" shouldFire() threw`, err);
          shouldRun = false;
        }
      }

      if (shouldRun) {
        notify(req.id);
        await runWithTimeout(req);
        notify(null);
        // Inter-item gap. Skipped after the LAST item to keep
        // queue-empty turnaround tight.
        if (queue.length > 0) await wait(INTER_ITEM_GAP_MS);
      }

      inflightById.delete(req.id);
      resolve();
    }
  } finally {
    draining = false;
  }
}

/**
 * Submit a request to the attention queue. The returned promise
 * resolves when YOUR run() completes (or is skipped because
 * shouldFire returned false). Same id while in flight → deduped to
 * the same promise.
 */
export function requestAttention(req: AttentionRequest): Promise<void> {
  const existing = inflightById.get(req.id);
  if (existing !== undefined) return existing;

  let resolveFn!: () => void;
  const promise = new Promise<void>((resolve) => {
    resolveFn = resolve;
  });
  inflightById.set(req.id, promise);
  queue.push({ req, resolve: resolveFn });

  // Kick the drain loop. We don't await it — it's the queue's job
  // to run independently; callers await the promise we returned.
  void drain();

  return promise;
}

/**
 * Is something currently holding the screen? Useful for ambient
 * animations that should defer (e.g. NIPE balloon shouldn't pop
 * while the welcome overlay is fading).
 */
export function isAttentionActive(): boolean {
  return currentlyRunning !== null;
}

/**
 * Get the id of the current attention holder, or null. Same info
 * as the value passed to subscribers.
 */
export function getCurrentAttention(): AttentionId | null {
  return currentlyRunning;
}

/**
 * Subscribe to attention transitions. Called with the new active id
 * when an item starts, then with null when it ends. Returns the
 * unsubscribe function.
 */
export function subscribeAttention(fn: Subscriber): () => void {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}

/**
 * Test-only: reset all internal state. Tests need this to start
 * each case with a clean queue.
 */
export function __resetAttentionForTests(): void {
  queue.length = 0;
  inflightById.clear();
  subscribers.clear();
  currentlyRunning = null;
  draining = false;
}

// Vite HMR — explicitly clear the queue on hot reload so the next
// reload starts from a clean state instead of inheriting stale
// in-flight resolvers from the previous module instance.
if (typeof import.meta !== 'undefined') {
  const hot = (
    import.meta as unknown as {
      hot?: { dispose: (cb: () => void) => void };
    }
  ).hot;
  if (hot !== undefined) {
    hot.dispose(() => {
      __resetAttentionForTests();
    });
  }
}
